/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Dialog } from '@headlessui/react'
import { createContext, useContext, useState } from 'react'
import type { PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'

import AlertIcon from '@Icons/alert-icon.svg?react'
import { useMeQuery } from '@Store/api'
import Mixpanel from '@Utils/mixpanel/Mixpanel'

import XIcon from '../../../assets/images/icons/x-close-filter.svg?react'

interface SPRuleTemplateWarningState {
  isOpen: boolean
  template?: {
    id: number
    title: string
    message: string
  }
  haveEnabledTemplate: boolean
  isShopify: boolean
  isRepricingEnabled: boolean
}

interface SPRuleTemplateWarningDispatch {
  open: (template?: SPRuleTemplateWarningState['template']) => void
  close: () => void
  onApply: (callback?: () => void) => void
  setHaveEnabledTemplate: (haveEnabled: boolean) => void
}

const SPRuleTemplateWarningStateContext =
  createContext<SPRuleTemplateWarningState>({} as SPRuleTemplateWarningState)
SPRuleTemplateWarningStateContext.displayName = 'SPRuleTemplateWarningState'

const SPRuleTemplateWarningDispatchContext =
  createContext<SPRuleTemplateWarningDispatch>(
    {} as SPRuleTemplateWarningDispatch,
  )
SPRuleTemplateWarningDispatchContext.displayName =
  'SPRuleTemplateWarningDispatch'

function SPRuleTemplateWarningProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [template, setTemplate] =
    useState<SPRuleTemplateWarningState['template']>(undefined)
  const [haveEnabledTemplate, setHaveEnabledTemplate] = useState<boolean>(false)
  const [applyCallback, setApplyCallback] = useState<() => void>(() => {
    setIsOpen(false)
  })

  const { data: meData } = useMeQuery()

  const open = (rule: SPRuleTemplateWarningState['template']): void => {
    setTemplate(rule)
    setIsOpen(true)
  }

  const close = (): void => {
    setIsOpen(false)
  }

  const onApply = (callback?: () => void): void => {
    setApplyCallback(() => () => {
      setIsOpen(false)
      !!callback && callback()
    })
  }

  return (
    <SPRuleTemplateWarningStateContext.Provider
      value={{
        isOpen,
        template,
        haveEnabledTemplate,
        isShopify: meData?.register_source === 'Shopify',
        isRepricingEnabled: !!meData?.shopify_set_prices,
      }}
    >
      <SPRuleTemplateWarningDispatchContext.Provider
        value={{ open, close, onApply, setHaveEnabledTemplate }}
      >
        {children}
        <SPRuleTemplateWarningModal onApplyClick={applyCallback} />
      </SPRuleTemplateWarningDispatchContext.Provider>
    </SPRuleTemplateWarningStateContext.Provider>
  )
}

function useSPRuleTemplateWarningState(): SPRuleTemplateWarningState {
  try {
    const context = useContext(SPRuleTemplateWarningStateContext)
    return context
  } catch (error) {
    throw new Error(
      'useSPRuleTemplateWarningState must be used within a SPRuleTemplateWarningProvider',
    )
  }
}

function useSPRuleTemplateWarningDispatch(): SPRuleTemplateWarningDispatch {
  try {
    const context = useContext(SPRuleTemplateWarningDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useSPRuleTemplateWarningDispatch must be used within a SPRuleTemplateWarningProvider',
    )
  }
}

function SPRuleTemplateWarningModal({
  onApplyClick,
}: {
  onApplyClick: () => void
}): JSX.Element {
  const {
    isOpen,
    template,
    // haveEnabledTemplate,
    isShopify,
    isRepricingEnabled,
  } = useSPRuleTemplateWarningState()
  const { close /*, setHaveEnabledTemplate */ } =
    useSPRuleTemplateWarningDispatch()

  const handleClose = (): void => {
    sendEvent('Rule Template Warning Canceled')
    close()
  }

  const handleApply = (): void => {
    sendEvent('Rule Template Warning Applied')
    // setHaveEnabledTemplate(true)
    close()
    onApplyClick()
  }

  const sendEvent = (
    event: string,
    additionalData?: Record<string, any>,
  ): void => {
    Mixpanel.track(event, {
      page: 'Rule Template Activation Modal',
      name: template?.title,
      id: template?.id,
      ...(additionalData || {}),
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      id="SPRuleTemplateWarningModal"
      initialFocus={undefined}
    >
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          background: 'rgba(41, 41, 41, 0.7)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* {haveEnabledTemplate ? (
          <AnotherEnabledTemplateWarningModal
            handleApply={handleApply}
            handleClose={handleClose}
          />
        ) : (
          <TemplateWarningModal
            template={template}
            handleApply={handleApply}
            handleClose={handleClose}
            isShopify={isShopify}
            isRepricingEnabled={isRepricingEnabled}
          />
        )} */}
        <TemplateWarningModal
          template={template}
          handleApply={handleApply}
          handleClose={handleClose}
          isShopify={isShopify}
          isRepricingEnabled={isRepricingEnabled}
        />
      </div>
    </Dialog>
  )
}

const TemplateWarningModal = ({
  template,
  handleApply,
  handleClose,
  isShopify,
  isRepricingEnabled,
}: {
  template: SPRuleTemplateWarningState['template']
  handleApply: () => void
  handleClose: () => void
  isShopify: boolean
  isRepricingEnabled: boolean
}) => (
  <Dialog.Panel className="relative border rounded-xl shadow-xl bg-white-100 md:w-2/3 lg:w-[55%] xxl:w-[40%] !max-w-screen-sm w-full">
    <div className="lg:inline-flex lg:flex-row lg:space-x-6 lg:items-start lg:w-full flex flex-col justify-center items-center relative p-6">
      <div className="w-full">
        <div className="flex w-full justify-center items-center mt-2">
          <h1 className="font-medium text-lg text-gray-900">
            Are you sure you want to activate <b>{template?.title}</b>?
          </h1>
          <button
            className="absolute right-4 top-4 active:bg-gray-50 p-2 outline-none"
            onClick={handleClose}
          >
            <XIcon />
          </button>
        </div>
        <div className="flex w-full items-start my-6">
          <div className="mr-5">
            <AlertIcon className="[&>path]:stroke-orange-500 w-12 h-12" />
          </div>
          <div className="text-gray-700">
            <p className="mb-3">{template?.message}</p>
            {isShopify && (
              <p className="mb-3">
                {isRepricingEnabled ? (
                  <>
                    Price updates will be applied directly to your Shopify store
                    immediately after rule activation. You can manage instant
                    repricing anytime in Shopify Settings.
                  </>
                ) : (
                  <>
                    Instant repricing is currently turned off for your Shopify
                    store, so calculated Smart Prices will not be updated
                    automatically. You can enable instant repricing anytime in
                    Shopify Settings to apply price updates automatically.
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="lg:inline-flex lg:flex-row lg:justify-end flex flex-col justify-between lg:space-x-6 w-full">
          {isShopify && (
            <Link
              className="w-60 text-center lg:px-10 py-2.5 bg-transparent !border !border-solid !border-primary-600 text-primary-600 rounded-lg"
              to="/settings/shopify-settings"
            >
              Go to Shopify Settings
            </Link>
          )}
          <button
            type="button"
            className="w-60 text-center lg:px-10 py-2.5 bg-primary-600 border-solid border-primary-600 text-white rounded-lg"
            onClick={handleApply}
          >
            Activate Rule
          </button>
        </div>
      </div>
    </div>
  </Dialog.Panel>
)

const AnotherEnabledTemplateWarningModal = ({
  handleApply,
  handleClose,
}: {
  handleApply: () => void
  handleClose: () => void
}) => (
  <Dialog.Panel className="relative border rounded-xl shadow-xl bg-white-100 md:w-2/3 lg:w-[55%] xxl:w-[40%] !max-w-screen-sm w-full">
    <div className="lg:inline-flex lg:flex-row lg:space-x-6 lg:items-start lg:w-full flex flex-col justify-center items-center relative p-6">
      <div className="w-full">
        <div className="flex w-full justify-center items-center mt-2">
          <h1 className="font-medium text-lg text-gray-900">
            Strategy Overlap Detected
          </h1>
        </div>
        <div className="flex w-full items-start my-6">
          <div className="mr-5">
            <AlertIcon className="[&>path]:stroke-orange-500 w-12 h-12" />
          </div>
          <div className="text-gray-700">
            <p className="mb-3">
              Some of the position groups used by this template are already
              managed by another Smart Pricing Template that is currently
              active. This means the same products could be affected by multiple
              pricing logics.
            </p>
            <p>
              For clearer results and easier control, we recommend using one
              pricing strategy at a time.
            </p>
          </div>
        </div>
        <div className="lg:inline-flex lg:flex-row lg:justify-end flex flex-col justify-between lg:space-x-6 w-full">
          <button
            type="button"
            className="w-full lg:px-10 lg:w-auto py-2.5 bg-primary-600 border-solid border-primary-600 text-white rounded-lg"
            onClick={handleApply}
          >
            Continue & Apply
          </button>
          <button
            type="button"
            className="w-full lg:px-10 lg:w-auto border border-solid border-gray-300 rounded-lg py-2.5 text-gray-500"
            onClick={handleClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </Dialog.Panel>
)

export {
  SPRuleTemplateWarningModal,
  SPRuleTemplateWarningProvider,
  useSPRuleTemplateWarningState,
  useSPRuleTemplateWarningDispatch,
}
